import { useQuery } from "@tanstack/react-query";
import { ACTIVE_COLUMNS, CLOSED_COLUMN_LABEL, CLOSED_COLUMNS, RECENT_COMPLETED_DAYS, type BoardCountsDTO, type BoardScope, type ClosedResult, type TableroColumn } from "@bgreen/shared";
import { LayoutGrid, List, ListFilter, Plus } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { columnLabel, columnsFor, columnTotal, DesktopBoard, MobileBoard, type ColumnKey } from "../components/board/BoardColumns";
import { BoardFilters } from "../components/board/Filters";
import { ListView } from "../components/board/ListView";
import { useNewMission } from "../components/NewMission";
import { Button } from "../components/ui/button";
import { Eyebrow } from "../components/ui/primitives";
import { get } from "../lib/api";
import { activeFilterCount, clearOpened, EMPTY_STATE, filterParams, peekOpened, readState, useBoardCounts, useBoardState, type BoardState } from "../lib/board";
import { cn } from "../lib/cn";
import { useCompany } from "../lib/company";
import { useIsMobile, useMe } from "../lib/hooks";
import { ROLE_LABEL } from "../lib/labels";
import type { ProjectOverview } from "../lib/types";

const Segment = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button role="tab" aria-selected={active} onClick={onClick}
    className={cn("inline-flex h-10 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[9px] border-0 px-3.5 text-[14px] font-medium", "max-sm:h-auto max-sm:min-h-14 max-sm:flex-col max-sm:gap-0 max-sm:whitespace-normal max-sm:px-1 max-sm:py-1.5 max-sm:text-center max-sm:text-[13px] max-sm:leading-tight", active ? "bg-ac text-acx" : "bg-transparent text-tx hover:bg-hv")}>{children}</button>
);
const Count = ({ n }: { n: number | undefined }) => <span className="font-mono text-[12px] opacity-80">{n ?? "–"}</span>;
const Pill = ({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) => (
  <button aria-pressed={on} onClick={onClick} className={cn("h-9 cursor-pointer rounded-full border px-3.5 text-[13.5px]", on ? "border-ac bg-ac text-acx" : "border-ln2 bg-transparent text-tx hover:bg-hv")}>{children}</button>
);

/**
 * Tablero de Misiones: presentación y navegación sobre el estado real. No hay arrastre entre fases:
 * el backend es quien avanza una misión (resultado del agente, CI, aprobaciones).
 */
export function MissionsPage() {
  const { state, update, reset } = useBoardState();
  const { companyId, qs } = useCompany();
  const me = useMe();
  const mobile = useIsMobile();
  const { open } = useNewMission();
  const role = me.data?.user.role;
  const canCreate = role === "admin" || role === "operator";
  const [showFilters, setShowFilters] = useState(false);

  const projects = useQuery({ queryKey: ["projects", companyId], queryFn: () => get<ProjectOverview[]>(`/projects${qs()}`) });
  const filters = useMemo(() => filterParams(state, companyId, me.data?.user.id), [state, companyId, me.data?.user.id]);
  const counts = useBoardCounts(filters);
  const c: BoardCountsDTO | undefined = counts.data;

  // Si cambia la empresa, un proyecto/repositorio que ya no pertenece al ámbito se descarta en vez de dejar un tablero vacío sin explicación.
  useEffect(() => {
    const list = projects.data;
    if (!list) return;
    if (state.project && !list.some((p) => p.id === state.project)) update({ project: "", repo: "" });
    else if (state.repo && !list.filter((p) => !state.project || p.id === state.project).some((p) => p.repositories.some((r) => r.id === state.repo))) update({ repo: "" });
  }, [projects.data, state.project, state.repo, update]);

  // Al volver desde el detalle: se resalta la misión abierta y se lleva a la vista.
  const [selectedId, setSelectedId] = useState<string | null>(peekOpened);
  useEffect(() => {
    if (!selectedId) return;
    let tries = 0;
    const find = setInterval(() => {
      const el = document.querySelector(`[data-mission-id="${selectedId}"]`);
      if (el) { el.scrollIntoView({ block: "center", inline: "center" }); clearInterval(find); } else if (++tries > 20) clearInterval(find);
    }, 150);
    const done = setTimeout(() => { setSelectedId(null); clearOpened(); }, 6000);
    return () => { clearInterval(find); clearTimeout(done); };
  }, [selectedId]);

  const scope = state.scope;
  const setScope = (s: BoardScope) => update({ scope: s, col: "", result: "" });
  const applyView = (params: string) => update({ ...EMPTY_STATE, ...(readState(new URLSearchParams(params)) as Partial<BoardState>), col: "" });
  const colTotal = (k: ColumnKey) => columnTotal(c, scope, k) ?? 0;
  // Móvil: Tablero y Necesita mi intervención usan `col`; Cerradas usa `result` (la primera pestaña es «Completadas»).
  const mobileCol: ColumnKey = scope === "closed" ? state.result || CLOSED_COLUMNS[0] : state.col || ACTIVE_COLUMNS.find((k) => colTotal(k) > 0) || "pending";
  const onMobileCol = (k: ColumnKey) => (scope === "closed" ? update({ result: k as ClosedResult }) : update({ col: k as TableroColumn }));
  const nFilters = activeFilterCount(state);
  const emptyText = scope === "attention"
    ? (role === "viewer" ? `Tu rol (${ROLE_LABEL.viewer}) solo consulta: no tienes acciones pendientes.` : "Nada requiere tu intervención con estos filtros.")
    : nFilters > 0 ? "Ninguna misión coincide con los filtros." : "Sin misiones.";
  const listMode = state.view === "list";

  return (
    <div className={cn("mx-auto flex w-full max-w-[1800px] flex-col px-4 pt-5 md:px-8 md:pt-7", mobile ? "min-h-full pb-24" : "min-h-[560px] pb-3")}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Eyebrow>Misiones</Eyebrow><h1 className="m-0 mt-2 font-display text-[30px] font-medium leading-[1.1] tracking-[-0.025em]">Tablero</h1></div>
        {canCreate && <Button variant="primary" onClick={() => open()}><Plus className="h-4 w-4" />Nueva misión</Button>}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Ámbito del tablero" className="grid w-full grid-cols-3 gap-1 rounded-[12px] border border-ln bg-sf p-1 sm:flex sm:w-auto">
          <Segment active={scope === "board"} onClick={() => setScope("board")}>
            Tablero
            {/* Dos contadores distintos: en curso y completadas recientes. */}
            <span className="font-mono text-[12px] opacity-80" title={`${c?.active ?? "–"} en curso · ${c?.recent ?? "–"} completadas en los últimos ${RECENT_COMPLETED_DAYS} días`}>
              <span className="max-sm:hidden">{c?.active ?? "–"} en curso · {c?.recent ?? "–"} recientes</span>
              <span className="flex flex-col sm:hidden"><span>{c?.active ?? "–"} en curso</span><span>{c?.recent ?? "–"} recientes</span></span>
            </span>
          </Segment>
          <Segment active={scope === "attention"} onClick={() => setScope("attention")}><span className="max-sm:hidden">Necesita mi intervención</span><span className="sm:hidden">Mi intervención</span> <Count n={c?.attention} /></Segment>
          <Segment active={scope === "closed"} onClick={() => setScope("closed")}>Cerradas <Count n={c?.closed.total} /></Segment>
        </div>
        <div role="tablist" aria-label="Vista" className="flex gap-1 rounded-[12px] border border-ln bg-sf p-1">
          <Segment active={state.view === "board"} onClick={() => update({ view: "board" })}><LayoutGrid className="h-4 w-4" />Tablero</Segment>
          <Segment active={state.view === "list"} onClick={() => update({ view: "list" })}><List className="h-4 w-4" />Lista</Segment>
        </div>
      </div>

      <div className="mt-3">
        {mobile && <button onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} className="mb-2 flex h-11 cursor-pointer items-center gap-2 rounded-[10px] border border-ln2 bg-transparent px-3.5 text-[14px] font-medium text-tx"><ListFilter className="h-4 w-4" />Buscar y filtrar{nFilters ? ` (${nFilters})` : ""}</button>}
        {(!mobile || showFilters) && <BoardFilters state={state} projects={projects.data ?? []} update={update} reset={reset} applyView={applyView} mobile={mobile} />}
      </div>

      {scope === "closed" && state.view === "list" && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Resultado">
          <Pill on={state.result === ""} onClick={() => update({ result: "" })}>Todas <span className="font-mono text-[12px] opacity-80">{c?.closed.total ?? "–"}</span></Pill>
          {CLOSED_COLUMNS.map((r) => <Pill key={r} on={state.result === r} onClick={() => update({ result: r })}>{CLOSED_COLUMN_LABEL[r]} <span className="font-mono text-[12px] opacity-80">{c?.closed.byResult[r] ?? "–"}</span></Pill>)}
        </div>
      )}
      {scope !== "closed" && state.view === "list" && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="Columna">
          <Pill on={state.col === ""} onClick={() => update({ col: "" })}>Todas <span className="font-mono text-[12px] opacity-80">{(scope === "attention" ? c?.attention : c ? c.active + c.recent : undefined) ?? "–"}</span></Pill>
          {columnsFor(scope).map((k) => <Pill key={k} on={state.col === k} onClick={() => update({ col: k as TableroColumn })}>{columnLabel(k)} <span className="font-mono text-[12px] opacity-80">{c ? colTotal(k) : "–"}</span></Pill>)}
        </div>
      )}

      <div className={cn("mt-3 flex flex-col", mobile || !listMode ? "" : "min-h-0 flex-1")}>
        {listMode ? (
          <div className={cn(mobile ? "" : "min-h-0 flex-1 overflow-y-auto")}>
            <ListView scope={scope} column={state.col} result={state.result} filters={filters} selectedId={selectedId} mobile={mobile} emptyText={emptyText} />
          </div>
        ) : mobile ? (
          <MobileBoard scope={scope} filters={filters} counts={c} selectedId={selectedId} col={mobileCol} onCol={onMobileCol} onOpenClosed={() => setScope("closed")} emptyText={emptyText} />
        ) : (
          <DesktopBoard scope={scope} filters={filters} counts={c} selectedId={selectedId} onOpenClosed={() => setScope("closed")} emptyText={emptyText} />
        )}
      </div>
    </div>
  );
}
